import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Shield, Users, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const HowWeWork = () => {
  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>How We Work | KGOB CPAs & Advisors</title>
        <meta name="description" content="Learn about KGOB's selective client onboarding process, who we serve best, and what it's like to work with our CPA firm." />
        <link rel="canonical" href="https://kgob.com/how-we-work" />
        <meta property="og:title" content="How We Work | KGOB CPAs & Advisors" />
        <meta property="og:description" content="Learn about KGOB's selective client onboarding process, who we serve best, and what it's like to work with our CPA firm." />
        <meta property="og:url" content="https://kgob.com/how-we-work" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Page Title & Introduction */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center">
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                Who We Serve — And How We Onboard the Right Fit Clients
              </h1>
              
              <p className="text-lg text-[#F7F6F2]/90 mb-6">
                At KGOB CPAs & Advisors, we're not a high-volume tax shop. We're a planning-focused CPA firm built for business owners and families who want a calm, long-term advisory relationship — not just a once-a-year tax filing. This page will help you understand who we serve best, how we work, and what to expect from our new client process.
              </p>
              
              <Card className="mt-8 p-6 border-2 border-[#D4AF37] bg-white/10 backdrop-blur-sm">
                <p className="text-[#F7F6F2] text-sm">
                  Want to know who we are behind the numbers? <Link data-testid="how-we-work-link-2" to="/our-story" className="text-[#D4AF37] hover:underline font-medium">Read our Firm Story & Mission</Link>
                </p>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Section 1 - About KGOB CPAs & Advisors */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Shield className="w-12 h-12 text-[#D4AF37]" />
              <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240]">
                About KGOB CPAs & Advisors
              </h2>
            </div>
            
            <p className="text-xl text-[#D4AF37] text-center mb-8 italic">
              Luxury advisory, precision compliance, and a calm place to land.
            </p>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-4">
                KGOB CPAs & Advisors is a North Carolina–based CPA firm serving clients across the United States. Our team combines:
              </p>
              
              <div className="space-y-4 mb-6">
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[#0B2240] font-semibold">Luxury advisory</p>
                    <p className="text-[#4C5A70] text-sm">Proactive guidance for business owners, professionals, and their families</p>
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <CheckCircle className="w-6 h-6 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[#0B2240] font-semibold">Precision compliance</p>
                    <p className="text-[#4C5A70] text-sm">High-quality tax, accounting, and payroll work handled with care and attention to detail</p>
                  </div>
                </div>
              </div>
              
              <p className="text-[#4C5A70] mb-4">
                We're here for people who want more than a checklist — clients who care about doing things the right way, staying ahead of the IRS, and making confident decisions about the next stage of their business and personal finances.
              </p>
              
              <div className="bg-[#F7F6F2] p-6 rounded-lg mt-6">
                <p className="text-[#0B2240] font-medium text-center">
                  Behind every return, projection, and report is a simple goal: protect your time, protect your peace of mind, and help you keep more of what you've built.
                </p>
              </div>
            </Card>
          </div>
        </section>

        {/* Section 2 - Who We Serve Best */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              Who We Serve Best
            </h2>
            
            <p className="text-xl text-[#D4AF37] text-center mb-12 italic">
              We're a great fit if this sounds like you...
            </p>
            
            <Card className="p-8 border-2 border-[#E1DFD7] mb-8">
              <p className="text-[#4C5A70] mb-6">
                We work best with clients who see us as part of their long-term strategy, not just a line item.
              </p>
              
              <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">For business relationships, that often means:</h3>
              
              <div className="space-y-4 mb-8">
                <div>
                  <p className="text-[#0B2240] font-semibold mb-2">Established, growth-minded small businesses</p>
                  <p className="text-[#4C5A70] text-sm">Service-based, professional, and trade businesses that have moved beyond "side hustle" and are serious about growth.</p>
                </div>
                
                <div>
                  <p className="text-[#0B2240] font-semibold mb-2">Owners who want clean, current financials</p>
                  <p className="text-[#4C5A70] text-sm">You understand that solid bookkeeping and payroll compliance are the foundation for tax savings, better decisions, and eventual exit or succession planning.</p>
                </div>
                
                <div>
                  <p className="text-[#0B2240] font-semibold mb-2">People who value proactivity over panic</p>
                  <p className="text-[#4C5A70] text-sm">You'd rather talk to your CPA before a big decision or tax surprise than scramble after the fact.</p>
                </div>
              </div>
              
              <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">For individual and family relationships, it often looks like:</h3>
              
              <div className="space-y-2 mb-6">
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span className="text-[#4C5A70]">High-earning W-2 or self-employed professionals</span>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span className="text-[#4C5A70]">Business owners and their families</span>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <span className="text-[#4C5A70]">Multi-state or more complex tax situations</span>
                </div>
              </div>
              
              <div className="bg-[#F7F6F2] p-6 rounded-lg mt-6">
                <p className="text-[#0B2240] font-medium mb-3">In all cases, our best client relationships share two traits:</p>
                <div className="space-y-2">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">Mutual respect for our team's time and expertise, and</span>
                  </div>
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">A genuine desire to build a long-term advisory partnership, not just "get this year's return done."</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Section 3 - What It's Like to Work With Us */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              What It's Like to Work With Us
            </h2>
            
            <p className="text-xl text-[#D4AF37] text-center mb-12 italic">
              Our commitments to you.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <Card className="p-6 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-4">When we agree to work together, you can expect:</h3>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#0B2240] font-semibold text-sm">A calm, organized experience</p>
                      <p className="text-[#4C5A70] text-xs">Clear onboarding, clear next steps, and a point of contact you can rely on</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#0B2240] font-semibold text-sm">Thoughtful, year-round planning</p>
                      <p className="text-[#4C5A70] text-xs">Not just filing, but looking ahead to help you make smarter tax and financial decisions</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#0B2240] font-semibold text-sm">Clean books as a baseline</p>
                      <p className="text-[#4C5A70] text-xs">For business clients, most relationships begin with bookkeeping and payroll compliance</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#0B2240] font-semibold text-sm">Responsive communication</p>
                      <p className="text-[#4C5A70] text-xs">Timely responses within business hours and in-person meetings when appropriate</p>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-4">What we ask in return:</h3>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <p className="text-[#4C5A70] text-sm">You're willing to share complete, accurate information in a timely manner</p>
                  </div>
                  
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <p className="text-[#4C5A70] text-sm">You respect deadlines, processes, and the expertise of our team</p>
                  </div>
                  
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <p className="text-[#4C5A70] text-sm">You're open to recommendations that protect you, even when they're not the most convenient in the moment</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Section 4 - New Client Fit & Onboarding Process */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              Our New Client Fit & Onboarding Process
            </h2>
            
            <p className="text-xl text-[#D4AF37] text-center mb-12 italic">
              A quick overview of the steps.
            </p>
            
            <Card className="p-8 border-2 border-[#E1DFD7] mb-8">
              <p className="text-[#4C5A70] mb-8">
                To protect the quality of our work and the experience of our existing clients, we use a structured process for all new relationships.
              </p>
              
              <div className="space-y-6">
                {[
                  {
                    step: '1',
                    title: 'Explore our firm',
                    description: 'Review this page and the rest of our site to get a feel for who we are, how we work, and the types of clients we serve best.'
                  },
                  {
                    step: '2',
                    title: 'Complete the New Client Fit & Onboarding Form',
                    description: 'When you\'re ready, you\'ll complete a secure online form that gathers key details about your business or personal situation, your goals, and your communication preferences.'
                  },
                  {
                    step: '3',
                    title: 'Internal review by our advisory team',
                    description: 'Our team reviews your information to evaluate: Whether your needs match our core services, and whether we have the right capacity and specialists available for you.'
                  },
                  {
                    step: '4',
                    title: 'Next steps and engagement',
                    description: 'If there\'s a strong mutual fit, we\'ll reach out with next steps, which may include a discovery call, proposal, and onboarding timeline. If we determine we\'re not the best firm for your needs at this time, we\'ll let you know and, when possible, offer general guidance on what to look for next.'
                  }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{item.title}</h3>
                      <p className="text-[#4C5A70] text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* Section 5 - Why We're Selective */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-6 text-center">
              Why We're Selective (In a Good Way)
            </h2>
            
            <p className="text-xl text-[#D4AF37] text-center mb-12 italic">
              We limit new relationships so we can truly take care of the ones we have.
            </p>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                We built KGOB to be a calm, steady partner — not a revolving door.
              </p>
              
              <p className="text-[#4C5A70] mb-6">
                To protect service quality, turnaround times, and the experience our current clients rely on, we can only accept a limited number of new relationships at any given time. That means:
              </p>
              
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">Completing the onboarding form does not guarantee acceptance, and</p>
                </div>
                <div className="flex gap-3">
                  <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                  <p className="text-[#4C5A70]">We may occasionally place new inquiries on a waitlist when our capacity is fully committed</p>
                </div>
              </div>
              
              <div className="bg-[#F7F6F2] p-6 rounded-lg">
                <p className="text-[#4C5A70] text-center">
                  We share this openly because we want to set clear expectations from day one and make sure that, if we do work together, we can show up at the level you deserve.
                </p>
              </div>
            </Card>
            
            <p className="text-sm text-[#4C5A70]/70 text-center mt-6 max-w-3xl mx-auto">
              Submitting this form does not create a CPA–client relationship and does not guarantee that we will be able to accept your engagement. It is the first step in exploring whether there is a strong mutual fit.
            </p>
          </div>
        </section>

        {/* Section 6 - Ready to Explore a Fit */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              Ready to Explore a Fit?
            </h2>
            
            <p className="text-xl text-[#D4AF37] mb-8 italic">
              If this resonates, we'd love to learn more about you.
            </p>
            
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              If what you've read here feels aligned with what you're looking for, the next step is simple:
            </p>
            
            <Button data-testid="how-we-work-button-1" asChild className="h-16 px-10 text-lg bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] mb-6">
              <Link data-testid="how-we-work-link-1" to="/book-consultation?source=how-we-work">Start the New Client Fit & Onboarding Form</Link>
            </Button>
            
            <p className="text-sm text-[#F7F6F2]/70 max-w-2xl mx-auto mb-8">
              This form typically takes a few minutes to complete. The more accurate and complete your responses, the better we can evaluate fit and recommend next steps.
            </p>
            
            <div className="border-t border-[#F7F6F2]/20 pt-8 mt-8">
              <p className="text-[#F7F6F2]/80">
                If at any point you're unsure whether KGOB is the right fit, you're always welcome to pause, gather your questions, and reach back out. We're here to help you make a confident, well-informed decision—whether that's with us or another trusted professional.
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default HowWeWork;